import React from 'react';
import { CheckCircle, FileText, Shield, Users, TrendingUp, Clock, Award } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const CorporateContracts = ({ onGetStarted }) => {
  const benefits = [
    'Expert contract drafting and review',
    'Risk assessment and mitigation strategies',
    'Negotiation support and guidance',
    'Customized contract templates',
    'Ongoing contract management support',
    'Compliance with regulatory requirements',
  ];

  const process = [
    {
      title: 'Initial Consultation',
      description: 'We discuss your business needs and contract requirements to understand your objectives.',
    },
    {
      title: 'Contract Drafting',
      description: 'Our attorneys prepare comprehensive contracts tailored to protect your interests.',
    },
    {
      title: 'Review & Revision',
      description: 'We collaborate with you to refine the contract terms and address any concerns.',
    },
    {
      title: 'Finalization',
      description: 'We finalize the contract and provide guidance for execution and implementation.',
    },
  ];

  const contractTypes = [
    'Vendor and Supplier Agreements',
    'Partnership Agreements',
    'Employment and Consulting Contracts',
    'Non-Disclosure Agreements (NDAs)',
    'Service Level Agreements (SLAs)',
    'Distribution Agreements',
    'Licensing Agreements',
    'Joint Venture Agreements',
  ];

  const whyChoose = [
    {
      icon: Award,
      title: 'Experienced Attorneys',
      description: 'Extensive experience in contract law across multiple industries.',
    },
    {
      icon: Shield,
      title: 'Risk Mitigation',
      description: 'Proactive approach to identifying and addressing potential legal risks.',
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: 'Efficient contract preparation without compromising quality or thoroughness.',
    },
    {
      icon: TrendingUp,
      title: 'Business-Focused',
      description: 'Legal solutions aligned with your business objectives and growth strategy.',
    },
  ];

  const faqs = [
    {
      question: 'What types of business contracts do you handle?',
      answer: 'We handle all types of business contracts including vendor agreements, partnership agreements, employment contracts, non-disclosure agreements, service agreements, distribution agreements, licensing agreements, and more. Our team has experience across virtually every type of commercial contract.',
    },
    {
      question: 'How long does contract drafting take?',
      answer: 'Timeline varies based on complexity, but most standard contracts are completed within 1-2 weeks. More complex agreements may take 3-4 weeks. We offer expedited services for urgent matters with rush turnaround available in as little as 48-72 hours.',
    },
    {
      question: 'Can you review contracts drafted by other parties?',
      answer: 'Yes, we provide comprehensive contract review services to identify potential issues, unfavorable terms, and legal risks. We will negotiate on your behalf to achieve more favorable terms and ensure your interests are protected.',
    },
    {
      question: 'What is included in your contract review service?',
      answer: 'Our contract review includes a thorough analysis of all terms and conditions, identification of potential risks and liabilities, assessment of compliance with applicable laws, recommendations for modifications, and a detailed review memo explaining our findings and recommendations.',
    },
    {
      question: 'Do you provide contract templates?',
      answer: 'Yes, we can create customized contract templates for your business that can be used for recurring transactions. These templates are specifically tailored to your business needs and include guidance on when and how to use them.',
    },
  ];

  return (
    <div className="pt-20" data-testid="service-corporate-contracts">
      {/* Hero Section with Background */}
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/8730998/pexels-photo-8730998.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">
              Business Law Services
            </div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">
              Corporate & Business Contracts
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Protect your business interests with expertly drafted and negotiated contracts. Our comprehensive contract services ensure your agreements are legally sound and aligned with your business objectives.
            </p>
            <button
              onClick={onGetStarted}
              className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium"
              data-testid="service-cta-button"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">
                Overview
              </h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">
                Well-drafted contracts are the foundation of successful business relationships. Our contract law attorneys bring decades of experience in drafting, reviewing, and negotiating a wide range of business agreements.
              </p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                We take a proactive approach to contract law, anticipating potential disputes and building protective provisions into every agreement. Whether you need a simple service agreement or a complex commercial contract, our team delivers precision and protection.
              </p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.pexels.com/photos/8441790/pexels-photo-8441790.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Corporate contract consultation" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Types of Contracts Section */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">
              Types of Contracts We Handle
            </h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">
              We provide comprehensive contract services across all business sectors and transaction types.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {contractTypes.map((type, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{type}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">
              Our Process
            </h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">
              A streamlined approach to contract development that ensures quality and efficiency.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">
                  {index + 1}
                </div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.unsplash.com/photo-1596574027151-2ce81d85af3e?w=800&q=80" alt="Why choose our corporate contract team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <ServiceFAQ faqs={faqs} />

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#2C3E50]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">
            Need Contract Legal Services?
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">
            Contact us today for expert contract drafting, review, and negotiation services.
          </p>
          <button
            onClick={onGetStarted}
            className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium"
            data-testid="service-bottom-cta"
          >
            Get Legal Help Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default CorporateContracts;
